/**
 * ELEMNT — Protección básica de imágenes del storefront
 * ---------------------------------------------------------------------------------
 * Encargo "Protección de imágenes": desalentar el guardado casual de las imágenes
 * visibles del sitio (product media, cards, Artist Pages, testimonios/prueba social,
 * Complete The Look, swatches de imagen, cualquier <img> editorial) sin tocar su
 * apariencia, calidad, proporción ni ninguna interacción existente (galería, lightbox,
 * zoom, sliders, swatches, formularios, navegación).
 *
 * 🔴 No es protección real: nada del frontend impide a alguien con DevTools, la caché
 * del navegador o herramientas externas obtener la imagen. Esto bloquea únicamente los
 * tres mecanismos más evidentes desde la interfaz normal de un visitante:
 *
 *   1. Clic derecho → "Guardar imagen como…"        (evento `contextmenu`)
 *   2. Arrastrar la imagen fuera de la página         (evento `dragstart`)
 *   3. Menú "Guardar/Copiar imagen" al mantener presionado en móvil, y el halo de
 *      selección al arrastrar sobre la imagen          (CSS: `-webkit-touch-callout`,
 *                                                        `-webkit-user-drag`, `user-select`)
 *
 * Alcance — solo <img> reales:
 *   - Nunca <svg> (iconos de UI, flechas, el logo cuando se inserta inline): no son
 *     `<img>`, así que ni el selector CSS ni `isImage()` los tocan.
 *   - Nunca imágenes de fondo CSS (hero, franjas editoriales que las usen): los
 *     navegadores ya las excluyen de "Guardar imagen como…" por su cuenta.
 *   - Los swatches de color (chip de `background-color`) no son `<img>` y no necesitan
 *     nada; los swatches de imagen sí son `<img>` y quedan cubiertos automáticamente,
 *     sin tener que tocar `snippets/swatch.liquid`.
 *
 * Mecánica: un único listener delegado en `document` por evento (`contextmenu`,
 * `dragstart`), más un único <style> inyectado una vez. Al ser delegación, cubre
 * cualquier imagen presente HOY o agregada DESPUÉS por un re-render de sección o por
 * JS del tema (media-gallery, quick-add, product-card) sin volver a engancharse — a
 * diferencia del patrón `guarded`/`WeakSet` que usan otros módulos de Elemnt (p. ej.
 * `elemnt-slideshow-guard.js`) para instrumentar un custom element concreto, aquí no
 * hace falta: no hay nada que inicializar por nodo.
 *
 * No interfiere con el arrastre existente del tema: `slideshow.js` (hero, prueba
 * social) arma su swipe sobre `pointerdown`/`pointermove`, y `comparison-slider.js`
 * (guía de hormas) sobre un `<input type="range">` — ninguno de los dos usa el
 * drag-and-drop nativo del navegador que `dragstart` gobierna. Bloquearlo aquí incluso
 * ayuda a esos dos: sin esto, el navegador puede interpretar el mismo gesto de arrastre
 * como "recoger la imagen" a mitad de un swipe con mouse.
 *
 * Se desactiva en el editor de temas (`request.design_mode`, ver el `<script>` que
 * carga este archivo en layout/theme.liquid): el fundador reactualiza y revisa
 * imágenes constantemente desde ahí mientras construye el sitio (V1 sale con mockups,
 * la fotografía real llega después) y no tiene sentido quitarle el clic derecho en su
 * propio flujo de edición — la protección solo importa de cara al visitante real.
 *
 * Un único archivo, sin dependencias de otros módulos de Elemnt ni del tema: se enlaza
 * una sola vez, globalmente, en layout/theme.liquid — igual que elemnt-tokens.css.
 */

const STYLE_ID = 'elemnt-image-guard-style';

/**
 * Inyecta la regla CSS una sola vez (protegido contra doble inyección si este módulo
 * llegara a evaluarse dos veces). `user-select: none` y `-webkit-user-drag: none` viven
 * en CSS global — no en un atributo `draggable="false"` por imagen vía Liquid— porque
 * así cubren toda imagen del tema, nativa de Horizon incluida, desde un solo punto en
 * vez de una regla por componente.
 */
function injectStyle() {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  // Ninguna de las tres propiedades cambia layout, tamaño ni apariencia pintada: son
  // puramente de comportamiento de interacción (selección/drag/callout).
  style.textContent = 'img{-webkit-user-drag:none;user-select:none;-webkit-touch-callout:none;}';
  document.head.appendChild(style);
}

/** @param {EventTarget | null} target */
function isImage(target) {
  return target instanceof Element && target.closest('img') !== null;
}

injectStyle();

// Clic derecho sobre una imagen: sin menú contextual nativo (ahí vive "Guardar imagen
// como…"). Sin `capture` ni `stopPropagation`: nada más en el tema escucha
// `contextmenu` sobre imágenes, así que no hay nada que neutralizar antes que esto.
document.addEventListener('contextmenu', (event) => {
  if (isImage(event.target)) event.preventDefault();
});

// Arrastrar una imagen fuera de la página (drag-and-drop nativo del navegador).
document.addEventListener('dragstart', (event) => {
  if (isImage(event.target)) event.preventDefault();
});
